/**
 * Impersonation context for the new dashboard (Vega).
 *
 * Admin impersonation is started in the legacy admin (login-admin-user). When
 * the legacy app hands the session over to Vega (?access_token=...), it now
 * also passes who the REAL admin is (impersonated=1&imp_by_*). The router
 * stores that context here so a global banner can show it on every page.
 *
 * Exiting impersonation is delegated back to the legacy app: the original
 * session stash lives in the legacy origin's localStorage, so we redirect to
 * VITE_VUE2_APP_URL/?exit_impersonation=1 and let it restore the session (it
 * bounces the restored admin straight back to Vega).
 */

const IMPERSONATION_KEY = 'impersonation';

export interface ImpersonationInfo {
  by: { name: string; email: string; role: string };
  started_at: string;
}

export const getImpersonation = (): ImpersonationInfo | null => {
  try {
    return JSON.parse(localStorage.getItem(IMPERSONATION_KEY) || 'null');
  } catch {
    return null;
  }
};

export const saveImpersonation = (info: ImpersonationInfo): void => {
  localStorage.setItem(IMPERSONATION_KEY, JSON.stringify(info));
};

export const clearImpersonation = (): void => {
  localStorage.removeItem(IMPERSONATION_KEY);
};

export const exitImpersonation = (): void => {
  clearImpersonation();
  window.location.href = `${import.meta.env.VITE_VUE2_APP_URL}/?exit_impersonation=1`;
};

/**
 * Start impersonating a user — delegated to the legacy admin, for the same
 * reason exiting is (AB#21160).
 *
 * `login-admin-user/{id}` mints a token for the target user, but calling it from
 * here and dropping the response is what made "Login as user" look like a no-op:
 * this origin kept the admin's token and the reload simply re-authenticated the
 * admin. Storing the returned token here would not fix it either. The two apps
 * are separate origins with separate localStorage: legacy would still hold the
 * admin's session, so a sidebar link to a legacy screen would silently drop out
 * of the impersonation, and "Exit impersonation" restores a stash that only
 * exists on that origin.
 *
 * So impersonation starts where it ends. Legacy swaps the session, stashes the
 * real admin, and its existing access-new-dashboard redirect hands the
 * impersonated token straight back here with the imp_by_* banner params — the
 * handoff the router guard already understands.
 */
export const startImpersonation = (userId: number): void => {
  window.location.href = `${import.meta.env.VITE_VUE2_APP_URL}/?impersonate=${encodeURIComponent(
    String(userId)
  )}`;
};

/**
 * Parse the legacy-admin handoff params synchronously at module load — i.e.
 * BEFORE the app mounts, before the router's initial navigation runs, and
 * before any component reads the impersonation state. The router guard also
 * processes these params, but it runs after the app shell (and the banner)
 * has already mounted, which made the banner miss the very first load.
 * Only acts on handoff loads (an access_token param is present); ordinary
 * in-app navigations never reach this code because it runs once per page load.
 */
const initImpersonationFromHandoffUrl = (): void => {
  try {
    const params = new URLSearchParams(window.location.search);
    if (!params.get('access_token')) return;
    if (params.get('impersonated') === '1') {
      saveImpersonation({
        by: {
          name: params.get('imp_by_name') || '',
          email: params.get('imp_by_email') || '',
          role: params.get('imp_by_role') || '',
        },
        started_at: params.get('imp_started_at') || '',
      });
    } else {
      clearImpersonation();
    }
  } catch {
    /* never block app boot over this */
  }
};

initImpersonationFromHandoffUrl();
